import React, { useEffect, useState } from "react";
import { Flex, Box, AspectRatio, Text, type IconProps } from "@chakra-ui/react";
import { type StaticImageData } from "next/image";
import { stagger, useAnimate, useInView } from "framer-motion";
import * as Sentry from "@sentry/nextjs";
import { FillImage } from "components/images";
import { FacebookIcon, SnapchatIcon, TikTokIcon } from "ui/icons/integrations";
import mobileImage from "../../../../../public/assets/platform/customer-studio/hero-mobile.png";
import firstSourceRowImage from "../../../../../public/assets/platform/customer-studio/hero-source-row-1.png";
import secondSourceRowImage from "../../../../../public/assets/platform/customer-studio/hero-source-row-2.png";
import thirdSourceRowImage from "../../../../../public/assets/platform/customer-studio/hero-source-row-3.png";
import { ConnectorLine } from "./connector-line";

const rowHeight = "82px";
const scaleFadeIn = { opacity: 1, scale: 1 };
const scaleFadeOut = { opacity: 0, scale: 0.95 };
const moveRight = { opacity: [0, 0.8, 1], x: ["-20%", "800%"] };
const resetTiming = { duration: 0.05 };

type Source = {
  name: string;
  image: StaticImageData;
};

type Audience = {
  name: string;
  icon: (props: IconProps) => JSX.Element;
};

const sources: Source[] = [
  {
    name: "Indie music fans",
    image: firstSourceRowImage,
  },
  {
    name: "Concert attendees",
    image: secondSourceRowImage,
  },
  {
    name: "Newsletter subscribers",
    image: thirdSourceRowImage,
  },
];

const audiences: Audience[] = [
  {
    name: "Add to audience",
    icon: TikTokIcon,
  },
  {
    name: "Conversion action",
    icon: FacebookIcon,
  },
  {
    name: "Add to suppression",
    icon: SnapchatIcon,
  },
];

function useHeroAnimation(isActive: boolean) {
  const [scope, animate] = useAnimate();

  useEffect(() => {
    const triggerAnimation = async () => {
      try {
        await animate(
          isActive
            ? [
                // Animation sequence
                [".audience-item", scaleFadeIn, { at: "+0.5", type: "spring", delay: stagger(0.4) }],
                [".connector-line", { opacity: [0, 1], x: ["0%", "100%"] }, { at: "-1.6", delay: stagger(0.4) }],
                ["#ball-0", moveRight, { at: "-0.5", duration: 1.1, ease: "easeInOut" }],
                ["#ball-1", moveRight, { at: "-0.4", duration: 0.9, ease: "easeInOut" }],
                ["#ball-2", moveRight, { at: "-0.5", duration: 1.2, ease: "easeInOut" }],
              ]
            : [
                // Reset
                [".audience-item", scaleFadeOut, resetTiming],
                [".connector-line", { opacity: 0, x: "0%" }, resetTiming],
              ],
        );
        if (isActive) {
          await animate(
            // Infinite loop after main sequence
            [
              ["#ball-0", moveRight, { duration: 1.1, ease: "easeInOut" }],
              ["#ball-1", moveRight, { at: "-0.4", duration: 0.9, ease: "easeInOut" }],
              ["#ball-2", moveRight, { at: "-0.5", duration: 1.2, ease: "easeInOut" }],
            ],
            { repeat: Number.POSITIVE_INFINITY, repeatDelay: -0.2 },
          );
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    // Do not need `await` because there is no action after the promise
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    triggerAnimation();
  }, [isActive, animate]);

  return scope;
}

export function HeroAnimation() {
  const [animationActive, setAnimationActive] = useState(false);
  const scope = useHeroAnimation(animationActive);
  const isInView = useInView(scope, { amount: 0.25, once: true });

  useEffect(() => {
    setAnimationActive(isInView);
  }, [isInView]);

  return (
    <Flex ref={scope} boxSize="100%" maxW="600px" mx="auto" pos="relative">
      <AspectRatio display={{ base: "block", md: "none" }} ratio={1408 / 975} pos="relative" w="100%">
        <FillImage src={mobileImage} alt="Illustration of custom audiences getting activated through Hightouch." />
      </AspectRatio>

      <Flex display={{ base: "none", md: "flex" }} align="end" justify="space-between" w="100%">
        <Box
          flex={1}
          bg="#F7FAF9"
          borderRadius="smaller"
          p="small"
          boxShadow="0px 24.453px 67.455px 0px rgba(0, 0, 0, 0.07), 0px 9.419px 21.486px 0px rgba(0, 0, 0, 0.04), 0px 1.992px 5.496px 0px rgba(0, 0, 0, 0.03), 0px 1.992px 5.496px 0px rgba(0, 0, 0, 0.03)"
        >
          <Flex
            align="center"
            color="#006D97"
            textStyle="caption1"
            fontWeight="semibold"
            gap="smaller"
            justify="space-between"
          >
            <Text borderRadius="6px" bg="new.blue.900" color="white" p="tiny">
              Audiences
            </Text>
            <Text>Attributes</Text>
            <Text>Events</Text>
          </Flex>

          <Flex direction="column" gap={3} mt={5}>
            {sources.map(({ name, image }) => (
              <Flex
                key={name}
                bg="white"
                borderRadius="tiny"
                direction="column"
                gap="tiny"
                h={rowHeight}
                py="12px"
                px="20px"
                pos="relative"
                overflow="hidden"
                filter="drop-shadow(0px 18.902px 18.902px rgba(13, 54, 79, 0.08))"
                backdropFilter="blur(9.451029777526855px)"
                zIndex={1}
              >
                <Box pos="absolute" left={0} top={0} bottom={0} w="5px" bg="new.blue.300" />
                <Text textStyle="caption1" color="#006D97">
                  {name}
                </Text>
                <AspectRatio ratio={20 / 3} pos="relative" w="100%" maxW={{ base: "200px", lg: "170px", xl: "200px" }}>
                  <FillImage
                    src={image}
                    alt={`Faces of ${name}.`}
                    placeholder="empty"
                    style={{ objectFit: "contain" }}
                  />
                </AspectRatio>
              </Flex>
            ))}
          </Flex>
        </Box>

        <Flex direction="column" gap="smallest" flex={1} pb={{ base: 5, lg: 3, xl: 5 }} pl={8}>
          {audiences.map(({ name, icon }, index) => (
            <Flex key={name} pos="relative">
              <Box
                pos="absolute"
                h="1px"
                w="60px" // Width = audience container left padding + source right padding + 1px for the border
                right="100%"
                top="50%"
                bottom="50%"
                transform="translateY(-50%)"
              >
                <ConnectorLine className="connector-line" pos="absolute" right="100%" w="100%" opacity={0} />
                <Box
                  id={`ball-${index}`}
                  pos="absolute"
                  right="110%"
                  top="-3.5px"
                  borderRadius="full"
                  boxSize="10px"
                  bg="new.blue.700"
                />
              </Box>

              <Flex
                className="audience-item"
                align="center"
                bg="brand.background.light"
                borderRadius="smaller"
                boxShadow="0px 24.453px 67.455px 0px rgba(0, 0, 0, 0.07), 0px 9.419px 21.486px 0px rgba(0, 0, 0, 0.04), 0px 1.992px 5.496px 0px rgba(0, 0, 0, 0.03), 0px 1.992px 5.496px 0px rgba(0, 0, 0, 0.03)"
                h={rowHeight}
                justify="center"
                p={{ base: "smallest", lg: "tiny", xl: "smallest" }}
                pos="relative"
                w="100%"
              >
                <Flex
                  align="center"
                  bg="white"
                  borderRadius="6px"
                  boxSize="100%"
                  gap="smaller"
                  p={{ base: 3, lg: 2.5, xl: 3 }}
                  zIndex={1}
                >
                  <Box as={icon} boxSize={{ base: "big", xl: "bigger" }} />
                  <Box>
                    <Text textStyle="caption1" fontWeight="semibold">
                      {name}
                    </Text>
                    <Text textStyle="caption2" color="brand.textSecondary.light" mt="tiniest">
                      Today
                    </Text>
                  </Box>
                </Flex>
              </Flex>
            </Flex>
          ))}
        </Flex>
      </Flex>
    </Flex>
  );
}
